<!--
  =====================================================================
  SECTION HORIZONTAL STRIP - Striscia orizzontale homepage
  =====================================================================
  
  Sezione orizzontale con immagine hero e cards.
  Il contenuto cambia in base all'hover delle sezioni superiori.
  
  PROPS:
  - content: Object - Dati sezione dal backend
    - left: { image, alt, cards[] }
    - right: { image, alt, cards[] }
  
  FEATURES:
  - Immagine hero che cambia con transizione fade
  - Cards dinamiche basate su hover state
  - Default: mostra contenuto left (Cassa)
  
  =====================================================================
-->

<template>
  <section class="section-horizontal-strip">
    <div class="strip-hero">
      <img 
        v-show="state.hoverSection === 'left'"
        :src="content.left.image" 
        :alt="content.left.alt"
        class="strip-hero__image"
      />
      <img 
        v-show="state.hoverSection === 'right'"
        :src="content.right.image" 
        :alt="content.right.alt"
        class="strip-hero__image"
      />
    </div>

    <div class="strip-content">
      <div class="cards-container">
        <a
          v-for="(card, index) in currentCards"
          :key="index"
          :href="card.link"
          class="card"
          rel="noopener noreferrer"
        >
          <h3 class="card__title">{{ card.title }}</h3>
          <p class="card__description">{{ card.description }}</p>
        </a>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import { state } from '@/store/index.js'

const props = defineProps({
  content: {
    type: Object,
    required: true
  }
})

const currentCards = computed(() => {
  return state.hoverSection === 'left' 
    ? props.content.left.cards 
    : props.content.right.cards
})
</script>

<style scoped lang="scss">
@import "@/assets/styles/partials/variables";
@import "@/assets/styles/partials/mixins";

.section-horizontal-strip {
  position: relative;
  background-color: $clr-brand-dark;
  height: 25vh; // Ridotto da 30vh

  @include respond-below(lg) {
    display: none;
  }

  @include respond-below(xl) {
    height: 24vh;
  }
}

.strip-hero {
  position: absolute;
  bottom: calc(100% - 1px);
  left: 0;
  right: 0;
  width: 100%;
  background: $clr-brand-dark;
}

.strip-hero__image {
  width: 100%;
  height: auto;
  display: block;
  transition: opacity 0.6s ease;
}

.strip-content {
  @include flex-center;
  padding: $space-3 $space-2;
  height: 100%;
  background-color: $clr-brand-dark;

  @media (max-width: 1399px) and (min-width: 1200px) {
    padding: 44px 30px;
  }

  @media (max-width: 1199px) and (min-width: 1000px) {
    padding: 40px 28px;
  }

  @media (max-width: 999px) and (min-width: 800px) {
    padding: 32px 24px;
  }
  
  @include respond-below(xl) {
    padding: $space-2-5 $space-1-75;
  }
  
  @include respond-below(lg) {
    padding: $space-2 $space-1-5;
  }
  
  @include respond-below(md) {
    padding: $space-2 $space-1;
  }
  
  @include respond-below(sm) {
    padding: $space-1-5 $space-0-75;
  }
}

.cards-container {
  display: flex;
  gap: $space-2;
  max-width: 1200px;
  width: 100%;
  justify-content: center;

  @media (max-width: 1399px) and (min-width: 1200px) {
    gap: 26px;
    max-width: 997px;
  }

  @media (max-width: 1199px) and (min-width: 1000px) {
    gap: 21px;
    max-width: 803px;
  }

  @media (max-width: 999px) and (min-width: 800px) {
    gap: 18px;
    max-width: 700px;
  }
  
  @include respond-below(xl) {
    gap: $space-1-75;
    max-width: 1100px;
  }
  
  @include respond-below(lg) {
    gap: $space-1-5;
    max-width: 950px;
  }
  
  @include respond-below(md) {
    flex-direction: column;
    gap: $space-1;
  }
  
  @include respond-below(sm) {
    gap: $space-0-75;
  }
}

.card {
  flex: 1;
  max-width: 350px;
  padding: $space-1-5;
  background: $clr-white;
  border: 1px solid $border-color;
  border-radius: 0; // Squadrate per coerenza
  text-decoration: none;
  color: $text-color;
  @include hover-lift;
  border-color: rgba($clr-brand-dark, 0.3);

  @media (max-width: 1399px) and (min-width: 1200px) {
    max-width: 290px;
    padding: 1.25rem;
  }

  @media (max-width: 1199px) and (min-width: 1000px) {
    max-width: 234px;
    padding: 1.0rem;
  }

  @media (max-width: 999px) and (min-width: 800px) {
    max-width: 203px;
    padding: 0.8rem;
  }
  
  @include respond-below(xl) {
    max-width: 320px;
    padding: $space-1-25;
  }
  
  @include respond-below(lg) {
    max-width: 280px;
    padding: $space-1;
  }
  
  @include respond-below(md) {
    max-width: 100%;
    padding: $space-1-25;
  }
  
  @include respond-below(sm) {
    padding: $space-1;
  }
}

.card__title {
  font-family: 'Karla', sans-serif;
  font-size: $font-lg;
  font-weight: 600;
  color: $clr-brand-dark;
  margin: 0 0 $space-0-5 0;

  @media (max-width: 1399px) and (min-width: 1200px) {
    font-size: 1.15rem;
  }

  @media (max-width: 1199px) and (min-width: 1000px) {
    font-size: 0.92rem;
  }

  @media (max-width: 999px) and (min-width: 800px) {
    font-size: 0.80rem;
  }
  
  @include respond-below(xl) {
    font-size: clamp(1.4rem, 2.6vw, 1.8rem);
  }
  
  @include respond-below(lg) {
    font-size: clamp(1.25rem, 2.4vw, 1.6rem);
  }
  
  @include respond-below(md) {
    font-size: clamp(1.3rem, 2.8vw, 1.7rem);
  }
  
  @include respond-below(sm) {
    font-size: clamp(1.15rem, 3.2vw, 1.4rem);
  }
}

.card__description {
  font-size: $font-base;
  line-height: var(--line-height-base);
  color: rgba($text-color, 0.8);
  margin: 0;

  @media (max-width: 1399px) and (min-width: 1200px) {
    font-size: 1.0rem;
  }

  @media (max-width: 1199px) and (min-width: 1000px) {
    font-size: 0.80rem;
  }

  @media (max-width: 999px) and (min-width: 800px) {
    font-size: 0.70rem;
  }
  
  @include respond-below(xl) {
    font-size: clamp(1.15rem, 2.2vw, 1.5rem);
  }
  
  @include respond-below(lg) {
    font-size: clamp(1.05rem, 2vw, 1.35rem);
  }
  
  @include respond-below(md) {
    font-size: clamp(1.1rem, 2.3vw, 1.45rem);
  }
  
  @include respond-below(sm) {
    font-size: clamp(1rem, 2.6vw, 1.25rem);
  }
}
</style>
